import React, { useState, useEffect, useMemo, useRef } from "react";
import "./schedule.css";
import { Row, Col, Button, Form, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedDate,
  setSelectedFacility,
  setSelectedSport,
  setSelectedPitch,
  setPitches,
  setWeeklySlots
} from "../../redux/scheduleSlice";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchPitches, selectAllPitches } from "../../redux/viewPitchSlice";
import { fetchFacilitiesForFilters, fetchSportsForFilters, selectFacilities, selectSports } from "../../redux/slices/pitchFiltersSlice";
import {
  getSlotsByDate,
  getSlotBookingDetails,
  editSlot,
  deleteSlotDetailIndex,
  cancelSpecificBooking,
  cancelMultipleSlots,
  reserveSlotExistingUser,
  reserveSlotRandomUser,
  searchUser
} from "../../services/api";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Skeleton from "react-loading-skeleton";

// SVG Imports
import dateicon from "../../assets/svgs/date-icon.svg";
import timeicon from "../../assets/svgs/clock-min-icon.svg";
import sportsicon from "../../assets/sidepanel/sports.svg";
import pitch from "../../assets/sidepanel/pitch.svg";
import facilityflag from "../../assets/sidepanel/flag.svg";
import priceicon from "../../assets/svgs/price-icon.svg";
import gendericon from "../../assets/svgs/gender-icon.svg";
import smallcal from "../../assets/svgs/smallest-date-icon.svg";
import smallid from "../../assets/sidepanel/idcard.svg";
import phoneicon from "../../assets/svgs/phone-icon.svg";
import cross from "../../assets/svgs/cross-btn.svg";
import editing from "../../assets/svgs/edit-btn.svg";
import profilePlaceholder from "../../assets/svgs/user-placeholder.svg"; // Placeholder for user image
import PopupEdit from "../../assets/svgs/popupedit.svg";
import BlockIcon from "../../assets/svgs/blockbooking.svg";
import TrashIcon from "../../assets/svgs/slot-delete.svg";
import CancelIcon from "../../assets/svgs/cross-btn.svg";
import CopyIcon from "../../assets/svgs/file.svg"; // Using file icon as clipboard copy icon

const VIEW_MODE_KEY = "schedule-view-mode";

const BookingSchedule = () => {
  const dispatch = useDispatch();

  // Redux state
  const {
    selectedFacility,
    selectedSport,
    selectedPitch,
    weeklySlots,
    weeklyLoading
  } = useSelector((state) => state.schedule);

  // Get facilities and sports from pitchFiltersSlice
  const facilities = useSelector(selectFacilities);
  const sports = useSelector(selectSports);

  // Get all pitches from viewPitchSlice
  const allPitches = useSelector(selectAllPitches); // eslint-disable-line no-unused-vars

  // Local state
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [datePickerDate, setDatePickerDate] = useState(new Date());
  const [availablePitches, setAvailablePitches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localPitches, setLocalPitches] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [reserveModal, setReserveModal] = useState(false);
  const [reserveMode, setReserveMode] = useState("existing"); // 'existing' or 'random'
  const [reserveIdentifier, setReserveIdentifier] = useState("");
  const [reserveEmail, setReserveEmail] = useState("");
  const [reserveIdentifierType, setReserveIdentifierType] = useState("id"); // 'id' or 'email'
  const [reserveNotes, setReserveNotes] = useState("");
  const [randomUserName, setRandomUserName] = useState("");
  const [randomUserPhone, setRandomUserPhone] = useState("");
  const [randomUserEmail, setRandomUserEmail] = useState("");
  const [randomUserNtn, setRandomUserNtn] = useState("");
  const [randomUserNotes, setRandomUserNotes] = useState("");
  const [reserveLoading, setReserveLoading] = useState(false);

  // User search states
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [bookingDetailLoading, setBookingDetailLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Cancel modal and reason
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // View mode: 'comfortable' (original) or 'dense' (time-aligned timetable)
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(VIEW_MODE_KEY) === "dense" ? "dense" : "comfortable";
    } catch {
      return "comfortable";
    }
  });
  const [hoveredBookingId, setHoveredBookingId] = useState(null);

  // Refs
  const clickToken = useRef(0);      // guards against out-of-order booking-detail responses
  const searchTimer = useRef(null);  // debounce timer for user search
  const latestQuery = useRef("");    // latest search text (ignore stale responses)
  const searchRef = useRef(null);    // always points to the newest handleUserSearch

  const changeViewMode = (mode) => {
    document.querySelectorAll('.multi-slot-connection').forEach(el => el.remove());
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      /* storage unavailable - ignore */
    }
  };

  // Toast helper
  const notify = (message, type = 'info') => {
    toast.dismiss();
    setTimeout(() => {
      const options = {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false
      };
      if (type === 'success') {
        toast.success(message, { ...options, toastId: 'success-toast' });
      } else {
        toast.error(message, { ...options, toastId: 'error-toast' });
      }
    }, 100);
  };

  // Fetch facilities, sports, and pitches on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [, , pitchesRes] = await Promise.all([
          dispatch(fetchFacilitiesForFilters()),
          dispatch(fetchSportsForFilters()),
          dispatch(fetchPitches())
        ]);

        if (pitchesRes.payload) {
          setAvailablePitches(pitchesRes.payload);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load facilities, sports, and pitches. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  // Get filtered pitches based on selected facility and sport
  const getFilteredPitches = () => {
    if (!selectedFacility || !selectedSport || !facilities || !sports || !availablePitches) {
      return [];
    }

    const selectedFacilityData = facilities.find(f => f._id === selectedFacility);
    const selectedSportData = sports.find(s => s._id === selectedSport);

    if (!selectedFacilityData?.name || !selectedSportData?.name) {
      return [];
    }

    return availablePitches.filter(p => {
      if (!p?.facilityName || !p?.sportName) return false;
      const facilityNameMatch = String(p.facilityName).toLowerCase() === String(selectedFacilityData.name).toLowerCase();
      const sportNameMatch = String(p.sportName) === String(selectedSportData.name);
      return facilityNameMatch && sportNameMatch;
    });
  };

  // Get filtered sports based on selected facility
  const getFilteredSports = () => {
    if (!selectedFacility) return [];
    const selectedFacilityData = facilities.find(f => f._id === selectedFacility);
    if (!selectedFacilityData || !selectedFacilityData.availableSports) return [];

    return sports.filter(sport =>
      selectedFacilityData.availableSports.some(availableSport =>
        availableSport._id === sport._id
      )
    );
  };

  // Update pitches when facility or sport changes
  useEffect(() => {
    if (selectedFacility && selectedSport) {
      const filteredPitches = getFilteredPitches();
      setLocalPitches(filteredPitches);
      if (JSON.stringify(filteredPitches) !== JSON.stringify(localPitches)) {
        dispatch(setPitches(filteredPitches));
      }
    } else if (localPitches.length > 0) {
      setLocalPitches([]);
      dispatch(setPitches([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacility, selectedSport, availablePitches]);

  // Fetch weekly slots when date, facility, sport, or pitch changes
  useEffect(() => {
    if (!(selectedFacility && selectedSport && selectedPitch)) return;
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getSlotsByDate({
          from_date: moment(datePickerDate).startOf('isoWeek').format('MM-DD-YYYY'),
          to_date: moment(datePickerDate).startOf('isoWeek').add(6, 'days').format('MM-DD-YYYY'),
          facility: selectedFacility,
          sport: selectedSport,
          pitch: selectedPitch
        });
        if (ignore) return;
        if (response?.data?.status && response.data.slots) {
          dispatch(setWeeklySlots(response.data.slots));
        }
      } catch (err) {
        if (ignore) return;
        console.error("Error fetching slots:", err);
        // Never keep another pitch's slots on screen after a failure
        dispatch(setWeeklySlots([]));
        setError("Failed to fetch slots. Please try again.");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => { ignore = true; };
  }, [selectedFacility, selectedSport, selectedPitch, datePickerDate, dispatch]);

  // Draw connection lines (comfortable view only) + redraw on resize
  useEffect(() => {
    document.querySelectorAll('.multi-slot-connection').forEach(el => el.remove());
    if (viewMode !== 'comfortable' || weeklySlots.length === 0 || loading || weeklyLoading) return;

    const t = setTimeout(drawMultiSlotConnections, 100);
    window.addEventListener('resize', drawMultiSlotConnections);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', drawMultiSlotConnections);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklySlots, loading, weeklyLoading, viewMode]);

  // Clear the pending search timer on unmount
  useEffect(() => () => clearTimeout(searchTimer.current), []);

  // Generate week days (always Monday -> Sunday of the selected week)
  const generateWeekDays = (date) => {
    const start = moment(date).startOf('isoWeek');
    return Array.from({ length: 7 }, (_, i) => {
      const day = start.clone().add(i, 'days');
      return {
        date: day.date(),
        day: day.format('ddd'),
        month: day.format('MMM'),
        fullDate: day.format('YYYY-MM-DD'),
        displayDate: day.format('MM-DD-YYYY')
      };
    });
  };

  const weekDays = generateWeekDays(datePickerDate);

  // Reset anything tied to the currently selected slot
  const resetSelection = () => {
    clickToken.current++;
    setSelectedSlot(null);
    setBookingDetailLoading(false);
    setShowCancelModal(false);
    setCancelReason("");
  };

  // Change week forward/backward
  const changeWeek = (offset) => {
    document.querySelectorAll('.multi-slot-connection').forEach(el => el.remove());
    const newDate = new Date(datePickerDate);
    newDate.setDate(datePickerDate.getDate() + offset * 7);
    setDatePickerDate(new Date(newDate));
    resetSelection();
  };

  // Fetch slots by date using the API (used for manual refresh & after actions)
  const fetchSlotsByDate = async (date) => {
    if (!selectedFacility || !selectedSport || !selectedPitch) return;

    try {
      setLoading(true);
      setError(null);

      const payload = {
        from_date: moment(date).startOf('isoWeek').format('MM-DD-YYYY'),
        to_date: moment(date).startOf('isoWeek').add(6, 'days').format('MM-DD-YYYY'),
        facility: selectedFacility,
        sport: selectedSport,
        pitch: selectedPitch
      };

      const response = await getSlotsByDate(payload);
      if (response?.data?.status && response.data.slots) {
        dispatch(setWeeklySlots(response.data.slots));
      }
    } catch (err) {
      console.error("Error fetching slots by date:", err);
      dispatch(setWeeklySlots([]));
      setError("Failed to fetch slots. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!selectedFacility || !selectedSport || !selectedPitch) {
      toast.info('Select facility, sport and pitch first');
      return;
    }
    document.querySelectorAll('.multi-slot-connection').forEach(el => el.remove());
    resetSelection();
    await fetchSlotsByDate(datePickerDate);
  };

  // Get names of selected items
  const selectedFacilityName = facilities.find(f => f._id === selectedFacility)?.name || "Select Facility";
  const selectedSportName = sports.find(s => s._id === selectedSport)?.name || "Select Sport";
  const selectedPitchName = localPitches.find(p => p._id === selectedPitch)?.name || "Select Pitch/Court";

  // ---------- Multi-slot connection lines (comfortable view) ----------
  function drawMultiSlotConnections() {
    document.querySelectorAll('.multi-slot-connection').forEach(el => el.remove());

    const container = document.querySelector('.slots-container');
    if (!container) return;

    const multiSlotGroups = {};
    document.querySelectorAll('.slot.multi-booked[data-booking-id]').forEach(slot => {
      const bookingId = slot.getAttribute('data-booking-id');
      if (bookingId && bookingId !== '') {
        if (!multiSlotGroups[bookingId]) multiSlotGroups[bookingId] = [];
        multiSlotGroups[bookingId].push(slot);
      }
    });

    Object.keys(multiSlotGroups).forEach(bookingId => {
      const slots = multiSlotGroups[bookingId];
      if (slots.length > 1) {
        slots.sort((a, b) => {
          const aRect = a.getBoundingClientRect();
          const bRect = b.getBoundingClientRect();
          if (Math.abs(aRect.top - bRect.top) > 10) return aRect.top - bRect.top;
          return aRect.left - bRect.left;
        });

        for (let i = 0; i < slots.length - 1; i++) {
          createConnectionLine(container, slots[i], slots[i + 1]);
        }
      }
    });
  }

  function createConnectionLine(container, slot1, slot2) {
    const rect1 = slot1.getBoundingClientRect();
    const rect2 = slot2.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const x1 = rect1.left - containerRect.left + rect1.width;
    const y1 = rect1.top - containerRect.top + rect1.height / 2;
    const x2 = rect2.left - containerRect.left;
    const y2 = rect2.top - containerRect.top + rect2.height / 2;

    if (Math.abs(y1 - y2) < 10) {
      // Horizontal line with arrow
      const line = document.createElement('div');
      line.className = 'multi-slot-connection';
      line.style.position = 'absolute';
      line.style.background = '#667eea';
      line.style.zIndex = '1';
      line.style.pointerEvents = 'none';
      line.style.left = x1 + 'px';
      line.style.top = (y1 - 1) + 'px';
      line.style.width = (x2 - x1) + 'px';
      line.style.height = '2px';

      const arrow = document.createElement('div');
      arrow.className = 'connection-arrow';
      arrow.style.position = 'absolute';
      arrow.style.right = '-6px';
      arrow.style.top = '-3px';
      arrow.style.width = '0';
      arrow.style.height = '0';
      arrow.style.borderLeft = '6px solid #667eea';
      arrow.style.borderTop = '4px solid transparent';
      arrow.style.borderBottom = '4px solid transparent';
      line.appendChild(arrow);

      container.appendChild(line);
    } else {
      // L-shaped connection for slots in different rows
      const midX = x1 + (x2 - x1) / 2;
      const makePart = (styles) => {
        const el = document.createElement('div');
        el.className = 'multi-slot-connection';
        el.style.position = 'absolute';
        el.style.background = '#667eea';
        el.style.zIndex = '1';
        el.style.pointerEvents = 'none';
        Object.assign(el.style, styles);
        container.appendChild(el);
      };

      makePart({ left: x1 + 'px', top: (y1 - 1) + 'px', width: (midX - x1) + 'px', height: '2px' });
      makePart({ left: (midX - 1) + 'px', top: Math.min(y1, y2) + 'px', width: '2px', height: Math.abs(y2 - y1) + 'px' });
      makePart({ left: midX + 'px', top: (y2 - 1) + 'px', width: (x2 - midX) + 'px', height: '2px' });
    }
  }

  // ---------- Slot helpers ----------
  const getSelectedFromTime = () => {
    if (!selectedSlot) return null;
    if (selectedSlot.from) return selectedSlot.from;
    if (typeof selectedSlot.time === 'string') {
      return selectedSlot.time.split(' - ')[0];
    }
    return null;
  };

  // Robustly parse date and time in multiple formats (12h/24h)
  const parseSlotDateTime = (dateStr, timeStr) => {
    const formats = [
      'YYYY-MM-DD hh:mm A',
      'YYYY-MM-DD h:mm A',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD H:mm'
    ];
    let dt = moment(`${dateStr} ${timeStr}`, formats, true);
    if (!dt.isValid()) {
      dt = moment(`${dateStr} ${timeStr}`);
    }
    return dt;
  };

  const isSlotPast = (dateStr, timeStr) => {
    const dt = parseSlotDateTime(dateStr, timeStr);
    if (!dt.isValid()) return false; // unparsable -> don't disable actions
    return dt.isBefore(moment());
  };

  // Find the day-slot document that contains the currently selected time
  const findSelectedDaySlot = () =>
    weeklySlots.find(slot => {
      const slotDate = moment(slot.date, 'MM-DD-YYYY').format('YYYY-MM-DD');
      return slotDate === selectedSlot?.date && slot.detail.some(detail => detail.from === getSelectedFromTime());
    });

  // Handle slot click
  const handleSlotClick = async (day, slotDetail) => {
    // Toggle off if the same slot is clicked again
    const selectedFromTime = getSelectedFromTime();
    if (
      selectedSlot &&
      selectedSlot.date === day.fullDate &&
      selectedFromTime === slotDetail.from
    ) {
      clickToken.current++;
      setSelectedSlot(null);
      setBookingDetailLoading(false);
      return;
    }

    const token = ++clickToken.current;

    try {
      const specificSlot = weeklySlots.find(slot => {
        const slotDate = moment(slot.date, 'MM-DD-YYYY').format('YYYY-MM-DD');
        return slotDate === day.fullDate && slot.detail.some(detail => detail.from === slotDetail.from);
      });

      const slotId = specificSlot?.id;
      const isPast = isSlotPast(day.fullDate, slotDetail.from);

      if (slotDetail.booked) {
        if (!slotId) {
          toast.error("Could not find the selected slot");
          return;
        }

        setBookingDetailLoading(true);
        const response = await getSlotBookingDetails(slotId, slotDetail.from);
        if (token !== clickToken.current) return; // a newer click took over

        if (response.data && response.data.status && response.data.slot) {
          const bookingDetails = response.data.slot;

          // Random (outside) user has a phone number; app user has gender/age
          const isRandomUser = bookingDetails.bookedBy?.phone && bookingDetails.bookedBy.phone !== "N/A";

          setSelectedSlot({
            date: day.fullDate,
            time: slotDetail.to ? `${slotDetail.from} - ${slotDetail.to}` : slotDetail.from,
            from: slotDetail.from,
            to: slotDetail.to || null,
            pitch: bookingDetails.pitch,
            sports: bookingDetails.sport,
            facility: bookingDetails.facility,
            price: `${bookingDetails.price} PKR`,
            paymentType: slotDetail.paymentType || "N/A",
            status: slotDetail.multiBooking ? "Multi-Slot Booked" : "Booked",
            isBooked: true,
            isMultiBooking: slotDetail.multiBooking || false,
            isBlocked: slotDetail.blocked,
            username: bookingDetails.bookedBy?.name || "N/A",
            gender: isRandomUser ? "N/A" : (bookingDetails.bookedBy?.gender || "N/A"),
            age: isRandomUser ? "N/A" : (bookingDetails.bookedBy?.age || "N/A"),
            phone: isRandomUser ? (bookingDetails.bookedBy?.phone || "N/A") : "N/A",
            id: bookingDetails.slotId,
            bookingId: bookingDetails.bookedBy?.bookId || bookingDetails.bookedBy?.bookingId || "N/A",
            numericBookingId: bookingDetails.bookedBy?.bookingId || null,
            profilePic: bookingDetails.bookedBy?.profilePic || profilePlaceholder,
            isRandomUser: isRandomUser,
            totalCombinedSlots: slotDetail.totalCombinedSlots || null,
            detailSlotId: slotId,
            detailIndex: slotDetail._id || slotDetail.detailIndex || null,
            isPast
          });
        } else {
          toast.error("Failed to fetch booking details");
        }
        setBookingDetailLoading(false);
      } else {
        // Non-booked slots: show basic details
        setBookingDetailLoading(false);
        setSelectedSlot({
          date: day.fullDate,
          time: slotDetail.to ? `${slotDetail.from} - ${slotDetail.to}` : slotDetail.from,
          from: slotDetail.from,
          to: slotDetail.to || null,
          pitch: selectedPitchName,
          sports: selectedSportName,
          facility: selectedFacilityName,
          price: `${slotDetail.price} PKR`,
          paymentType: slotDetail.paymentType || "N/A",
          status: slotDetail.blocked ? "Blocked" : "Available",
          isBooked: false,
          isBlocked: slotDetail.blocked,
          id: slotId,
          phone: "N/A",
          isRandomUser: false,
          isPast
        });
      }
    } catch (err) {
      if (token !== clickToken.current) return;
      console.error("Error handling slot click:", err);
      toast.error("Failed to fetch slot details");
      setBookingDetailLoading(false);
    }
  };

  // ---------- Filter handlers ----------
  const handleFacilitySelect = (facility) => {
    document.querySelectorAll('.multi-slot-connection').forEach(el => el.remove());
    dispatch(setSelectedFacility(facility._id));
    dispatch(setSelectedSport(""));
    dispatch(setSelectedPitch(""));
    dispatch(setWeeklySlots([]));
    resetSelection();
  };

  const handleSportSelect = (sport) => {
    document.querySelectorAll('.multi-slot-connection').forEach(el => el.remove());
    dispatch(setSelectedSport(sport._id));
    dispatch(setSelectedPitch(""));
    dispatch(setWeeklySlots([]));
    resetSelection();
  };

  const handlePitchSelect = (p) => {
    document.querySelectorAll('.multi-slot-connection').forEach(el => el.remove());
    dispatch(setSelectedPitch(p._id));
    dispatch(setWeeklySlots([]));
    resetSelection();
  };

  // The fetch effect reacts to datePickerDate, so no manual fetch here
  const handleDateSelect = (date) => {
    if (!date) return;
    document.querySelectorAll('.multi-slot-connection').forEach(el => el.remove());
    setDatePickerDate(date);
    dispatch(setSelectedDate(moment(date).format('MM-DD-YYYY')));
    resetSelection();
  };

  const filteredSports = getFilteredSports();

  // ---------- Actions ----------
  const handlePriceEdit = async () => {
    if (!editPrice) {
      notify("Please enter a new price", "error");
      return;
    }

    try {
      const selectedDaySlot = findSelectedDaySlot();

      if (!selectedDaySlot) {
        notify("Could not find the selected slot", "error");
        return;
      }

      const updatedSlotDetails = selectedDaySlot.detail.map(detail => ({
        price: detail.from === getSelectedFromTime() ? Number(editPrice) : Number(detail.price)
      }));

      const response = await editSlot({
        _id: selectedDaySlot.id,
        slot: updatedSlotDetails
      });

      if (response.data?.status) {
        notify(response.data?.message || "Price updated successfully!", "success");
        setSelectedSlot(null); // panel would otherwise show the old price
        if (selectedFacility && selectedSport && selectedPitch) {
          await fetchSlotsByDate(datePickerDate);
        }
      } else {
        notify("Failed to update price. Please try again.", "error");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update price. Please try again.";
      notify(errorMessage, "error");
    }

    setEditModal(false);
    setEditPrice("");
  };

  const handleDeleteSlot = async () => {
    try {
      const daySlot = findSelectedDaySlot();

      if (!daySlot) {
        notify("Could not find the selected slot", "error");
        return;
      }

      const timeSlot = daySlot.detail.find(detail => detail.from === getSelectedFromTime());

      if (!timeSlot || !timeSlot._id) {
        notify("Could not find the selected time slot", "error");
        return;
      }

      const response = await deleteSlotDetailIndex(daySlot.id, timeSlot._id);

      if (response.data.status) {
        setDeleteModal(false);
        setSelectedSlot(null);

        if (selectedFacility && selectedSport && selectedPitch) {
          await fetchSlotsByDate(datePickerDate);
        }

        notify("Slot deleted successfully!", "success");
      } else {
        notify(response.data.message || "Failed to delete slot", "error");
      }
    } catch (err) {
      console.error("Error deleting slot:", err);
      notify(err.response?.data?.message || "Failed to delete slot", "error");
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelBooking = async () => {
    if (!selectedSlot?.isBooked || !cancelReason.trim()) {
      notify("Please provide a reason for cancellation", "error");
      return;
    }

    try {
      setCancelLoading(true);

      const daySlot = findSelectedDaySlot();

      if (!daySlot) {
        notify("Could not find the selected slot", "error");
        return;
      }

      const timeSlotIndex = daySlot.detail.findIndex(detail => detail.from === getSelectedFromTime());
      const timeSlot = daySlot.detail[timeSlotIndex];

      if (timeSlotIndex === -1 || !timeSlot) {
        notify("Could not find the selected time slot", "error");
        return;
      }

      let response;

      if (timeSlot.multiBooking && selectedSlot.bookingId) {
        // Multi-slot cancellation - dedicated endpoint (bookingId + cancelReason)
        response = await cancelMultipleSlots(selectedSlot.bookingId, cancelReason);
      } else {
        response = await cancelSpecificBooking(
          daySlot.id,
          timeSlot._id || timeSlotIndex,
          cancelReason
        );
      }

      if (response.data.status) {
        notify(response.data.message || "Booking cancelled successfully!", "success");
        setSelectedSlot(null);
        setShowCancelModal(false);
        setCancelReason("");

        if (selectedFacility && selectedSport && selectedPitch) {
          await fetchSlotsByDate(datePickerDate);
        }
      } else {
        notify(response.data.message || "Failed to cancel booking", "error");
      }
    } catch (err) {
      console.log("Cancel booking error:", err);
      notify(err.response?.data?.message || "Failed to cancel booking", "error");
    } finally {
      setCancelLoading(false);
    }
  };

  // ---------- User search (debounced) ----------
  const handleUserSearch = async (value) => {
    if (!value.trim() || value.trim().length < 3) {
      setSearchedUser(null);
      setSearchError("");
      return;
    }

    if (reserveIdentifierType === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) {
        setSearchedUser(null);
        setSearchError("Please enter a valid email address");
        return;
      }
    }

    try {
      setSearchLoading(true);
      setSearchError("");

      const response = reserveIdentifierType === 'id'
        ? await searchUser(value.trim(), null)
        : await searchUser(null, value.trim());

      if (latestQuery.current !== value) return; // stale response

      if (response.data && response.data.status && response.data.users && response.data.users.length > 0) {
        const user = response.data.users[0];
        setSearchedUser({
          name: user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || 'N/A',
          phone: user.phone || 'N/A',
          email: user.email || 'N/A',
          identifier: user.identifier || 'N/A',
          profilePic: user.profilePic || profilePlaceholder
        });
      } else {
        setSearchedUser(null);
        setSearchError("No user found with the provided information");
      }
    } catch (err) {
      if (latestQuery.current !== value) return;
      console.error("Error searching user:", err);
      setSearchedUser(null);
      setSearchError(err.response?.data?.message || "Failed to search user");
    } finally {
      setSearchLoading(false);
    }
  };

  // Always call the newest closure (so it sees the current identifier type)
  searchRef.current = handleUserSearch;

  const throttledUserSearch = (value) => {
    latestQuery.current = value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchRef.current(value), 500);
  };

  const cancelPendingSearch = () => {
    latestQuery.current = "";
    clearTimeout(searchTimer.current);
  };

  // Copy booking information to clipboard
  const copyBookingToClipboard = async () => {
    if (!selectedSlot) {
      notify("No booking information to copy", "error");
      return;
    }

    try {
      const bookingText = `Date: ${selectedSlot.date}
Facility: ${selectedSlot.facility}
Sports: ${selectedSlot.sports}
Pitch/Court: ${selectedSlot.pitch}
Price: ${selectedSlot.price}`;

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(bookingText);
        notify("Slot information copied to clipboard!", "success");
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = bookingText;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          notify("Slot information copied to clipboard!", "success");
        } catch (err) {
          notify("Failed to copy to clipboard", "error");
        }

        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      notify("Failed to copy to clipboard", "error");
    }
  };

  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    setReserveIdentifier(value);
    throttledUserSearch(value);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setReserveEmail(value);
    throttledUserSearch(value);
  };

  const resetReserveForm = () => {
    cancelPendingSearch();
    setSearchedUser(null);
    setSearchError("");
    setSearchLoading(false);
    setReserveIdentifier("");
    setReserveEmail("");
    setReserveIdentifierType("id");
    setReserveNotes("");
    setRandomUserName("");
    setRandomUserPhone("");
    setRandomUserEmail("");
    setRandomUserNtn("");
    setRandomUserNotes("");
  };

  const handleCloseReserveModal = () => {
    setReserveModal(false);
    resetReserveForm();
  };

  const handleIdentifierTypeChange = (e) => {
    cancelPendingSearch();
    setReserveIdentifierType(e.target.value);
    setSearchedUser(null);
    setSearchError("");
    setSearchLoading(false);
    setReserveIdentifier("");
    setReserveEmail("");
  };

  // Handle reserve booking
  const handleReserveBooking = async () => {
    if (reserveMode === 'existing') {
      if (reserveIdentifierType === 'id' && !reserveIdentifier.trim()) {
        notify('Please enter user identifier', 'error');
        return;
      }

      if (reserveIdentifierType === 'email' && !reserveEmail.trim()) {
        notify('Please enter user email', 'error');
        return;
      }

      if (!searchedUser) {
        notify('Please search and select a valid user first', 'error');
        return;
      }
    } else if (reserveMode === 'random') {
      if (!randomUserName.trim()) {
        notify('Please enter user name', 'error');
        return;
      }

      if (!randomUserPhone.trim()) {
        notify('Please enter user phone', 'error');
        return;
      }

      if (!randomUserEmail.trim()) {
        notify('Please enter user email', 'error');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(randomUserEmail.trim())) {
        notify('Please enter a valid email address', 'error');
        return;
      }
    } else {
      notify('Please select a reserve mode', 'error');
      return;
    }

    try {
      setReserveLoading(true);

      const daySlot = findSelectedDaySlot();

      if (!daySlot) {
        notify("Could not find the selected slot", "error");
        return;
      }

      const timeSlotIndex = daySlot.detail.findIndex(detail => detail.from === getSelectedFromTime());
      const timeSlot = daySlot.detail[timeSlotIndex];

      if (timeSlotIndex === -1 || !timeSlot) {
        notify("Could not find the selected time slot", "error");
        return;
      }

      let payload = {
        detailSlotId: daySlot.id,
        detailIndex: timeSlot._id || timeSlotIndex,
      };

      if (reserveMode === 'existing') {
        payload.notes = reserveNotes.trim();

        if (reserveIdentifierType === 'id') {
          payload.identifier = reserveIdentifier.trim();
        } else {
          payload.email = reserveEmail.trim();
          if (searchedUser?.identifier && searchedUser.identifier !== 'N/A') {
            payload.identifier = searchedUser.identifier;
          }
        }
      } else if (reserveMode === 'random') {
        payload = {
          ...payload,
          name: randomUserName.trim(),
          phone: randomUserPhone.trim(),
          email: randomUserEmail.trim(),
          ntn: randomUserNtn.trim() || null,
          notes: randomUserNotes.trim(),
        };
      }

      const response = reserveMode === 'existing'
        ? await reserveSlotExistingUser(payload)
        : await reserveSlotRandomUser(payload);

      if (response.data && response.data.status) {
        notify(response.data.message || "Slot reserved successfully!", "success");
        setReserveModal(false);
        resetReserveForm();
        setSelectedSlot(null); // panel would otherwise still say "Available"

        if (selectedFacility && selectedSport && selectedPitch) {
          await fetchSlotsByDate(datePickerDate);
        }
      } else {
        notify(response.data.message || "Failed to reserve slot", "error");
      }
    } catch (err) {
      console.error("Reserve booking error:", err);
      notify(err.response?.data?.message || "Failed to reserve slot", "error");
    } finally {
      setReserveLoading(false);
    }
  };

  // ---------- Small render helpers ----------
  const SkeletonBookingInfo = () => (
    <div className="booking-info skeleton-mode">
      
      <div className="booking-imp-details">
        {[0, 1, 2].map((rowIdx) => (
          <Row key={rowIdx} className="mb-3">
            {[0, 1].map((colIdx) => (
              <Col lg={6} key={colIdx} className="mb-2">
                <Skeleton width={140} height={16} style={{ marginBottom: '6px' }} />
                <Skeleton width={120} height={18} />
              </Col>
            ))}
          </Row>
        ))}

        <div style={{ }}>
          <div className="booked-by-card-skeleton" style={{ padding: 20, border: '1px solid #e9ecef', borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ marginLeft: 12, flex: 1 }}>
                <Skeleton width={180} height={16} />
                <Skeleton width={100} height={12} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 15 }}>
              {[1, 2].map(i => (
                <div key={i}>
                  <Skeleton width={80} height={14} style={{ marginBottom: 6 }} />
                  <Skeleton width={120} height={16} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <Row className="d-flex justify-content-center w-100 mt-3">
          {[1, 2, 3].map(btn => (
            <Col key={btn} xs="auto">
              <Skeleton width={110} height={38} style={{ margin: '0 6px' }} />
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );

  // Found-user tag / loading / error shown under the identifier + email inputs
  const renderSearchFeedback = (onClear) => (
    <>
      {searchLoading && (
        <div className="search-loading">
          <Spinner animation="border" size="sm" />
          <span>Searching...</span>
        </div>
      )}
      {searchedUser && (
        <div className="user-found-tag">
          <div className="reserve-booking-avatar">
            {searchedUser.profilePic && searchedUser.profilePic !== profilePlaceholder ? (
              <img
                src={searchedUser.profilePic}
                alt="Profile"
                className="reserve-booking-profile-img"
              />
            ) : (
              <span className="reserve-booking-avatar-text">
                {searchedUser.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="user-info">
            <span className="user-name">{searchedUser.name}</span>
            <span className="user-phone">{searchedUser.phone}</span>
          </div>
          <button type="button" className="clear-search" onClick={onClear}>
            ×
          </button>
        </div>
      )}
      {searchError && !searchedUser && (
        <div className="search-error">{searchError}</div>
      )}
    </>
  );

  // Summary tiles used in the reserve + delete popups
  const renderSlotTiles = (withPrice) => (
    <div className="slot-summary-grid">
      <div className="day-date-item"><span className="day-name">Date</span><span className="date-value">{selectedSlot?.date}</span></div>
      <div className="day-date-item"><span className="day-name">Facility</span><span className="date-value">{selectedSlot?.facility}</span></div>
      <div className="day-date-item"><span className="day-name">Time</span><span className="date-value">{selectedSlot?.time}</span></div>
      <div className="day-date-item"><span className="day-name">Pitch</span><span className="date-value">{selectedSlot?.pitch}</span></div>
      <div className="day-date-item"><span className="day-name">Sport</span><span className="date-value">{selectedSlot?.sports}</span></div>
      {withPrice && (
        <div className="day-date-item"><span className="day-name">Price</span><span className="date-value">{selectedSlot?.price}</span></div>
      )}
    </div>
  );

  const reserveDisabled =
    reserveLoading ||
    (reserveMode === 'existing' && (
      (reserveIdentifierType === 'id' && !reserveIdentifier.trim()) ||
      (reserveIdentifierType === 'email' && !reserveEmail.trim())
    )) ||
    (reserveMode === 'random' && (!randomUserName.trim() || !randomUserPhone.trim() || !randomUserEmail.trim()));

  // ---------- Dense view ----------
  const bookingHue = (id) => {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
    return h;
  };

  const denseData = useMemo(() => {
    const cells = {};
    const times = new Map();
    const perDay = {};
    const stats = { total: 0, available: 0, booked: 0, multi: 0, blocked: 0, bookedValue: 0 };
    weekDays.forEach(d => { cells[d.fullDate] = {}; perDay[d.fullDate] = { total: 0, taken: 0 }; });

    weeklySlots.forEach(ds => {
      const day = weekDays.find(d => d.displayDate === ds.date);
      if (!day) return;
      (ds.detail || []).forEach(d => {
        cells[day.fullDate][d.from] = d;
        if (!times.has(d.from)) {
          times.set(d.from, {
            from: d.from,
            to: d.to || null,
            order: moment(d.from, ['hh:mm A', 'h:mm A', 'HH:mm']).valueOf()
          });
        }
        stats.total++;
        perDay[day.fullDate].total++;
        if (d.multiBooking || d.booked) {
          stats.booked++;
          perDay[day.fullDate].taken++;
          stats.bookedValue += Number(d.price) || 0;
          if (d.multiBooking) stats.multi++;
        } else if (d.blocked) {
          stats.blocked++;
        } else {
          stats.available++;
        }
      });
    });

    const rows = [...times.values()].sort((a, b) => a.order - b.order);
    return { cells, rows, perDay, stats };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklySlots, datePickerDate]);

  const renderDenseGrid = () => {
    const { cells, rows, perDay } = denseData;
    const todayKey = moment().format('YYYY-MM-DD');

    if (rows.length === 0) {
      return (
        <div className="text-center py-5">
          <p style={{ fontFamily: "Poppins" }}>No slots this week</p>
        </div>
      );
    }

    return (
      <div className="dense-wrap">
        <div className="dense-scroll">
          <div className="dense-grid">
            <div className="dense-corner" />
            {weekDays.map(day => {
              const pd = perDay[day.fullDate];
              const pct = pd.total ? (pd.taken / pd.total) * 100 : 0;
              return (
                <div key={day.fullDate} className={`dense-day-head ${day.fullDate === todayKey ? 'today' : ''}`}>
                  <div className="dd-name">{day.day}</div>
                  <div className="dd-date">{day.month} {day.date}</div>
                  <div className="dd-meter"><span style={{ width: `${pct}%` }} /></div>
                  <div className="dd-count">{pd.taken}/{pd.total} booked</div>
                </div>
              );
            })}

            {rows.map((row, rIdx) => (
              <React.Fragment key={row.from}>
                <div className="dense-time">
                  {row.from}
                  {row.to && <small>{row.to}</small>}
                </div>

                {weekDays.map(day => {
                  const col = cells[day.fullDate];
                  const d = col[row.from];
                  if (!d) {
                    return <div key={`${day.fullDate}-${row.from}`} className="dense-empty">–</div>;
                  }

                  const state = d.multiBooking ? 'is-multi' : d.booked ? 'is-booked' : d.blocked ? 'is-blocked' : 'is-available';
                  const past = isSlotPast(day.fullDate, d.from);
                  const isSelected = selectedSlot?.date === day.fullDate && selectedSlot?.from === d.from;
                  const bid = d.bookingId ? String(d.bookingId) : null;
                  const prev = rIdx > 0 ? col[rows[rIdx - 1].from] : null;
                  const next = rIdx < rows.length - 1 ? col[rows[rIdx + 1].from] : null;
                  const joinTop = bid && prev && String(prev.bookingId) === bid;
                  const joinBottom = bid && next && String(next.bookingId) === bid;
                  const price = Number(d.price);
                  const priceLabel = Number.isFinite(price) ? price.toLocaleString() : '—';
                  const label = d.multiBooking
                    ? `Multi${d.totalCombinedSlots ? ` ×${d.totalCombinedSlots}` : ''}`
                    : d.booked
                      ? 'Booked'
                      : d.blocked ? 'Blocked' : 'Available';
                  const title = `${d.from}${d.to ? ` – ${d.to}` : ''} • ${label} • PKR ${priceLabel}${past ? ' • Past' : ''}`;

                  return (
                    <button
                      key={`${day.fullDate}-${row.from}`}
                      type="button"
                      title={title}
                      aria-pressed={isSelected}
                      disabled={d.blocked}
                      className={[
                        'dense-cell', state,
                        past && 'past',
                        isSelected && 'selected',
                        joinTop && 'join-top',
                        joinBottom && 'join-bottom',
                        bid && hoveredBookingId === bid && 'link-hover'
                      ].filter(Boolean).join(' ')}
                      style={d.multiBooking && bid ? { borderLeftColor: `hsl(${bookingHue(bid)}, 65%, 55%)` } : undefined}
                      onClick={() => handleSlotClick(day, d)}
                      onMouseEnter={() => bid && setHoveredBookingId(bid)}
                      onMouseLeave={() => setHoveredBookingId(null)}
                    >
                      <span className="dc-top">
                        <span className="dc-price">{priceLabel}</span>
                      </span>
                      <span className="dc-label">{label}</span>
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ---------- Render ----------
  return (
    <>
      <h2 className="mb-4" style={{ textAlign: "center" }}>My Schedule</h2>

      {/* Slot Legend */}
      <div className="slot-legend">
        <div className="legend-item"><div className="legend-swatch" style={{ background: '#28a745' }} /><span>Available</span></div>
        <div className="legend-item"><div className="legend-swatch" style={{ background: '#667eea' }} /><span>Multi-Slot Booked</span></div>
        <div className="legend-item"><div className="legend-swatch" style={{ background: '#ff6b6b', border: '1px solid #e05666' }} /><span>Booked</span></div>
        <div className="legend-item"><div className="legend-swatch" style={{ background: '#6c757d' }} /><span>Blocked</span></div>
        {viewMode === 'dense' && (
          <div className="legend-item"><div className="legend-swatch legend-past" /><span>Past</span></div>
        )}
      </div>

      <Row className="mb-4 dropdownscourt searchandfilter schd">
        <Col md={2}>
          <Form.Select
            value={selectedFacility || ""}
            onChange={(e) => {
              const facility = facilities.find(f => f._id === e.target.value);
              if (facility) handleFacilitySelect(facility);
              else {
                dispatch(setSelectedFacility(""));
                dispatch(setSelectedSport(""));
                dispatch(setSelectedPitch(""));
                dispatch(setWeeklySlots([]));
                resetSelection();
              }
            }}
          >
            <option value="">Select Facility</option>
            {facilities.map((facility) => (
              <option key={facility._id} value={facility._id}>
                {facility.name}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Select
            value={selectedSport || ""}
            onChange={(e) => {
              const sport = sports.find(s => s._id === e.target.value);
              if (sport) handleSportSelect(sport);
              else {
                dispatch(setSelectedSport(""));
                dispatch(setSelectedPitch(""));
                dispatch(setWeeklySlots([]));
                resetSelection();
              }
            }}
            disabled={!selectedFacility}
          >
            <option value="">Select Sport</option>
            {filteredSports.map((sport) => (
              <option key={sport._id} value={sport._id}>
                {sport.name}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Select
            value={selectedPitch || ""}
            onChange={(e) => {
              const p = localPitches.find(x => x._id === e.target.value);
              if (p) handlePitchSelect(p);
              else {
                dispatch(setSelectedPitch(""));
                dispatch(setWeeklySlots([]));
                resetSelection();
              }
            }}
            disabled={!selectedSport || localPitches.length === 0}
          >
            <option value="">Select Pitch/Court</option>
            {localPitches.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={2}>
          <DatePicker
            selected={datePickerDate}
            onChange={handleDateSelect}
            dateFormat="MMMM d, yyyy"
            className="form-control schedule-filter"
            placeholderText="Select Date"
          />
        </Col>

        <Col md="auto">
          <div className="view-toggle" role="group" aria-label="Slot view">
            <button
              type="button"
              className={viewMode === 'comfortable' ? 'active' : ''}
              onClick={() => changeViewMode('comfortable')}
            >
              Comfortable
            </button>
            <button
              type="button"
              className={viewMode === 'dense' ? 'active' : ''}
              onClick={() => changeViewMode('dense')}
            >
              Dense
            </button>
          </div>
        </Col>
      </Row>

      {error && (
        <div className="alert alert-danger mt-3">
          {error}
        </div>
      )}

      <div className={`booking-schedule ${viewMode === 'dense' ? 'is-dense' : ''}`}>
        <div className="schedule-container">
          <div className="schedule">
            {/* Week Navigation */}
            <div className="week-navigation">
              <button className="nav-buttons-schedule" onClick={() => changeWeek(-1)}>← Previous Week</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button className="nav-buttons-schedule" onClick={() => changeWeek(1)}>Next Week →</button>
                <button className="nav-buttons-schedule refresh" onClick={handleRefresh}>⟲</button>
              </div>
            </div>

            {/* Days Header (comfortable view only - dense view has its own sticky header) */}
            {viewMode === 'comfortable' && (
              <div className="days-header">
                {weekDays.map((day) => {
                  const pd = denseData.perDay[day.fullDate] || { total: 0, taken: 0 };
                  const pct = pd.total ? (pd.taken / pd.total) * 100 : 0;
                  const todayKey = moment().format('YYYY-MM-DD');
                  return (
                    <div key={day.fullDate} className={`day-header ${day.fullDate === todayKey ? 'today' : ''}`}>
                      <div className="day-name">{day.day}</div>
                      <div className="date">{`${day.month} ${day.date}`}</div>
                      <div className="dd-meter"><span style={{ width: `${pct}%` }} /></div>
                      <div className="dd-count">{pd.taken}/{pd.total} booked</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Slots */}
            <div className="slots">
              {loading || weeklyLoading ? (
                <div className="slots-container">
                  {Array(7).fill(0).map((_, idx) => (
                    <div key={idx} className="day-slots">
                      <div className="time-slots">
                        {Array(5).fill(0).map((__, slotIdx) => (
                          <Skeleton
                            key={slotIdx}
                            width={80}
                            height={36}
                            style={{ margin: '4px', borderRadius: '4px' }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : !selectedFacility || !selectedSport || !selectedPitch ? (
                <div className="text-center py-5">
                  <p style={{ fontFamily: "Poppins" }}>Please select a facility, sport, and pitch to view slots</p>
                </div>
              ) : weeklySlots.length === 0 ? (
                viewMode === 'dense' ? (
                  <div className="text-center py-5">
                    <p style={{ fontFamily: "Poppins" }}>No slots this week</p>
                  </div>
                ) : (
                  <div className="slots-container">
                    {weekDays.map((day) => (
                      <div key={day.fullDate} className="day-slots">
                        <div className="time-slots">
                          {Array.from({ length: 7 }).map((_, idx) => (
                            <div key={`${day.fullDate}-empty-${idx}`} className="slot no-slot-cell">No slots</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : viewMode === 'dense' ? (
                renderDenseGrid()
              ) : (
                <div className="slots-container">
                  {(() => {
                    // Build a map of day -> flattened detail list
                    const dayDetailsMap = weekDays.reduce((acc, day) => {
                      const daySlots = weeklySlots.filter(s => s.date === day.displayDate);
                      const details = [];
                      daySlots.forEach(ds => {
                        (ds.detail || []).forEach(d => details.push(d));
                      });
                      details.sort((a, b) => moment(a.from, 'hh:mm A').diff(moment(b.from, 'hh:mm A')));
                      acc[day.fullDate] = details;
                      return acc;
                    }, {});

                    const maxRows = Math.max(1, ...Object.values(dayDetailsMap).map(list => list.length));

                    return weekDays.map((day) => {
                      const details = dayDetailsMap[day.fullDate] || [];
                      return (
                        <div key={day.fullDate} className="day-slots">
                          <div className="time-slots">
                            {Array.from({ length: maxRows }).map((_, rowIdx) => {
                              const slotDetail = details[rowIdx];
                              if (!slotDetail) {
                                return (
                                  <div key={`${day.fullDate}-empty-${rowIdx}`} className="slot no-slot-cell">
                                    No slots
                                  </div>
                                );
                              }
                              const multiBookingGroupClass = slotDetail.multiBooking
                                ? `multi-group-${slotDetail.bookingId || 'default'}`
                                : '';

                              const isSelected = selectedSlot?.date === day.fullDate && selectedSlot?.from === slotDetail.from;

                              const baseStateClass = slotDetail.multiBooking
                                ? 'multi-booked'
                                : slotDetail.booked
                                  ? 'booked'
                                  : slotDetail.blocked
                                    ? 'blocked'
                                    : 'available';

                              const slotClass = isSelected
                                ? `${baseStateClass} selected ${multiBookingGroupClass}`.trim()
                                : `${baseStateClass} ${multiBookingGroupClass}`.trim();
                              const slotTitle = slotDetail.booked ? 'Booked' : 'Available';
                              return (
                                <button
                                  key={`${day.fullDate}-${rowIdx}`}
                                  className={`slot ${slotClass}`}
                                  title={slotTitle}
                                  onClick={() => handleSlotClick(day, slotDetail)}
                                  disabled={slotDetail.blocked}
                                  data-booking-id={slotDetail.bookingId || ''}
                                  data-multi-booking={slotDetail.multiBooking ? 'true' : 'false'}
                                >
                                  {slotDetail.from}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Booking Information (Right Panel) */}
          <div className="booking-info">
            <div className="booking-head">
              <h3 className="booking-title-head">Booking Information</h3>
              {selectedSlot && (
                <p className="booking-status">{selectedSlot.status}</p>
              )}
            </div>

            {bookingDetailLoading ? (
              <SkeletonBookingInfo />
            ) : selectedSlot ? (
              <>
                <div className="booking-imp-details">
                  <Row>
                    <Col lg={6}>
                      <div className="schd-details">
                        <img className="schd-icon" src={dateicon} alt="Date" /><span className="booking-date-schd">Date</span>
                      </div>
                      <p className="data-schd">{selectedSlot.date}</p>
                    </Col>
                    <Col lg={6}>
                      <div className="schd-details">
                        <img className="schd-icon" src={timeicon} alt="Time" /><span className="booking-date-schd">Booking Time</span>
                      </div>
                      <p className="data-schd">{selectedSlot.time}</p>
                    </Col>
                    <hr />
                  </Row>

                  <Row>
                    <Col lg={6}>
                      <div className="schd-details">
                        <img className="schd-icon" src={sportsicon} alt="Sport" /><span className="booking-date-schd">Sports</span>
                      </div>
                      <p className="data-schd">{selectedSlot.sports}</p>
                    </Col>
                    <Col lg={6}>
                      <div className="schd-details">
                        <img className="schd-icon" src={pitch} alt="Pitch" /><span className="booking-date-schd">Pitch/Court</span>
                      </div>
                      <p className="data-schd">{selectedSlot.pitch}</p>
                    </Col>
                    <hr />
                  </Row>

                  <Row>
                    <Col lg={6}>
                      <div className="schd-details">
                        <img className="schd-icon" src={facilityflag} alt="Facility" /><span className="booking-date-schd">Facility</span>
                      </div>
                      <p className="data-schd">{selectedSlot.facility}</p>
                    </Col>
                    <Col lg={6}>
                      <div className="schd-details">
                        <img className="schd-icon" src={priceicon} alt="Price" /><span className="booking-date-schd">Price</span>
                      </div>
                      <p className="data-schd">{selectedSlot.price} ({selectedSlot.paymentType})</p>
                    </Col>
                    <hr />
                  </Row>

                  {selectedSlot.isBooked && (
                    <>
                      <h3 className="booking-title-head2">Booked By</h3>

                      <Row>
                        <Col lg={6}>
                          <div className="schd-details">
                            <img className="schd-icon" src={selectedSlot.profilePic} alt="Profile" /><span className="booking-date-schd">Name</span>
                          </div>
                          <p className="data-schd">{selectedSlot.username}</p>
                        </Col>
                        <Col lg={6}>
                          <div className="schd-details">
                            <img className="schd-icon" src={smallid} alt="Booking ID" /><span className="booking-date-schd">Booking ID</span>
                          </div>
                          <p className="data-schd">{selectedSlot.numericBookingId ?? selectedSlot.bookingId}</p>
                        </Col>
                        <hr />
                      </Row>

                      {!selectedSlot.isRandomUser && (
                        <Row>
                          <Col lg={6}>
                            <div className="schd-details">
                              <img className="schd-icon" src={gendericon} alt="Gender" /><span className="booking-date-schd">Gender</span>
                            </div>
                            <p className="data-schd">{selectedSlot.gender}</p>
                          </Col>
                          <Col lg={6}>
                            <div className="schd-details">
                              <img className="schd-icon" src={smallcal} alt="Age" /><span className="booking-date-schd">Age</span>
                            </div>
                            <p className="data-schd">{selectedSlot.age}</p>
                          </Col>
                          <hr />
                        </Row>
                      )}

                      <Row>
                        <Col lg={6}>
                          <div className="schd-details">
                            <img className="schd-icon" src={phoneicon} alt="Phone" /><span className="booking-date-schd">Phone</span>
                          </div>
                          <p className="data-schd">{selectedSlot.phone}</p>
                        </Col>

                        <Col lg={6}>
                          <div className="schd-details">
                            <img className="schd-icon" src={smallcal} alt="Booked slots" /><span className="booking-date-schd">Booked Slots</span>
                          </div>
                          <p className="data-schd">{selectedSlot.totalCombinedSlots || '1'} slot</p>
                        </Col>
                        <hr />
                      </Row>
                    </>
                  )}

                  {!selectedSlot.isPast && (
                    <Row className="d-flex justify-content-center align-items-center action-buttons-container">
                      {!selectedSlot.isBooked && !selectedSlot.isBlocked && (
                        <Button
                          className="slot-buttons schd block-btn"
                          onClick={() => setReserveModal(true)}
                        >
                          <img src={cross} className="schd-icon" alt="Reserve" /> Reserve
                        </Button>
                      )}

                      {!selectedSlot.isBooked && (
                        <Button
                          className="slot-buttons schd edit-btn"
                          onClick={() => setEditModal(true)}
                        >
                          <img src={editing} className="schd-icon" alt="Edit" /> Edit
                        </Button>
                      )}

                      {selectedSlot.isBooked && (
                        <Button
                          className="slot-buttons schd cancel-btn"
                          onClick={handleCancelClick}
                          disabled={cancelLoading}
                        >
                          <img src={CancelIcon} className="schd-icon" alt="Cancel" /> Cancel
                        </Button>
                      )}

                      {!selectedSlot.isBooked && !selectedSlot.isBlocked && (
                        <Button
                          className="slot-buttons schd delete-btn"
                          onClick={() => setDeleteModal(true)}
                        >
                          <img src={TrashIcon} className="schd-icon delete-icon" alt="Delete" /> Delete
                        </Button>
                      )}

                      {!selectedSlot.isBooked && (
                        <Button
                          className="slot-buttons schd copy-btn"
                          onClick={copyBookingToClipboard}
                        >
                          <img src={CopyIcon} className="schd-icon" alt="Copy" /> Copy
                        </Button>
                      )}
                    </Row>
                  )}
                </div>
              </>
            ) : (
              <p style={{ fontFamily: "Poppins" }}>Select a slot to see details.</p>
            )}
          </div>
        </div>
      </div>

      {/* Edit Popup */}
      <div className={`${editModal ? 'edit-popup' : 'hide-edit'}`}>
        <div className='edit-popup-container'>
          <div className="edit-popup-card">
            <div className="popup-header">
              <div className="popup-logo">
                <img src={PopupEdit} alt="" />
              </div>
              <div className="pop-up-heading">
                <span>Edit Booking</span>
                <p>Note: You can only change Price in bookings</p>
              </div>
            </div>
            <div className="popup-body">
              <div className="popup-form">
                <div className="popup-input half">
                  <span>Slot Time</span>
                  <input type="text" value={selectedSlot?.time || ''} disabled />
                </div>
                <div className="popup-input half">
                  <span>Current Price</span>
                  <input
                    type="text"
                    value={selectedSlot?.price ? selectedSlot.price.replace(' PKR', '') : ''}
                    disabled
                  />
                </div>
                <div className="popup-input half">
                  <span>Facility</span>
                  <input type="text" value={selectedSlot?.facility || ''} disabled />
                </div>
                <div className="popup-input half">
                  <span>Sports</span>
                  <input type="text" value={selectedSlot?.sports || ''} disabled />
                </div>
                <div className="popup-input">
                  <span>New Price</span>
                  <input
                    type="number"
                    placeholder='Enter new price'
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="popup-btns">
              <div
                className="popup-btn"
                onClick={() => {
                  setEditModal(false);
                  setEditPrice("");
                }}
              >
                <span>Cancel</span>
              </div>
              <div className="popup-btn edit-btn" onClick={handlePriceEdit}>
                <span>Edit</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reserve Popup */}
      <div className={`${reserveModal ? 'edit-popup' : 'hide-edit'}`}>
        <div className='edit-popup-container'>
          <div className="edit-popup-card">
            <div className="popup-header">
              <div className="popup-logo">
                <img src={BlockIcon} alt="" />
              </div>
              <div className="pop-up-heading">
                <span>Reserve Booking</span>
                <p>Are you sure you want to reserve this booking? This action cannot be undone.</p>
              </div>
            </div>
            <div className="popup-body">
              {renderSlotTiles(true)}

              <div className="popup-form">
                <div className="popup-input">
                  <span>Reserve Mode</span>
                  <select
                    value={reserveMode}
                    onChange={(e) => setReserveMode(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Select Mode</option>
                    <option value="existing">GoPlay User</option>
                    <option value="random">Outside User</option>
                  </select>
                </div>

                {reserveMode === "existing" && (
                  <>
                    <div className="popup-input">
                      <span>Identifier Type</span>
                      <select
                        value={reserveIdentifierType}
                        onChange={handleIdentifierTypeChange}
                        className="form-control"
                      >
                        <option value="id">User ID</option>
                        <option value="email">Email</option>
                      </select>
                    </div>

                    {reserveIdentifierType === "id" && (
                      <div className="popup-input">
                        <span>User Identifier</span>
                        <input
                          type="text"
                          placeholder='Enter user identifier'
                          value={reserveIdentifier}
                          onChange={handleIdentifierChange}
                          className={searchedUser ? 'has-user' : ''}
                        />
                        {renderSearchFeedback(() => {
                          cancelPendingSearch();
                          setSearchedUser(null);
                          setReserveIdentifier("");
                          setSearchError("");
                        })}
                      </div>
                    )}

                    {reserveIdentifierType === "email" && (
                      <div className="popup-input">
                        <span>User Email</span>
                        <input
                          type="email"
                          placeholder='Enter user email'
                          value={reserveEmail}
                          onChange={handleEmailChange}
                          className={searchedUser ? 'has-user' : ''}
                        />
                        {renderSearchFeedback(() => {
                          cancelPendingSearch();
                          setSearchedUser(null);
                          setReserveEmail("");
                          setSearchError("");
                        })}
                      </div>
                    )}
                  </>
                )}

                {reserveMode === "random" && (
                  <>
                    <div className="popup-two-col">
                      <div className="popup-input">
                        <span>User Name*</span>
                        <input
                          type="text"
                          placeholder='Enter user name'
                          value={randomUserName}
                          onChange={(e) => setRandomUserName(e.target.value)}
                        />
                      </div>

                      <div className="popup-input">
                        <span>User Phone*</span>
                        <input
                          type="tel"
                          placeholder='Enter user phone number'
                          value={randomUserPhone}
                          onChange={(e) => setRandomUserPhone(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="popup-two-col">
                      <div className="popup-input">
                        <span>Email*</span>
                        <input
                          type="email"
                          placeholder='Enter user email'
                          value={randomUserEmail}
                          onChange={(e) => setRandomUserEmail(e.target.value)}
                        />
                      </div>

                      <div className="popup-input">
                        <span>NTN</span>
                        <input
                          type="text"
                          placeholder='Enter NTN (optional)'
                          value={randomUserNtn}
                          onChange={(e) => setRandomUserNtn(e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="popup-btns">
              <div className="popup-btn" onClick={handleCloseReserveModal}>
                <span>Cancel</span>
              </div>
              <div
                className={`popup-btn block-btn ${reserveDisabled ? 'disabled' : ''}`}
                onClick={() => {
                  if (reserveDisabled) return;
                  handleReserveBooking();
                }}
                style={{ opacity: reserveDisabled ? 0.6 : 1 }}
              >
                <span>{reserveLoading ? 'Reserving...' : 'Reserve'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Popup */}
      <div className={`${deleteModal ? 'edit-popup' : 'hide-edit'}`}>
        <div className='edit-popup-container'>
          <div className="edit-popup-card">
            <div className="popup-header">
              <div className="popup-logo delete-modal-logo">
                <img src={TrashIcon} alt="" />
              </div>
              <div className="pop-up-heading">
                <span>Delete Booking</span>
                <p>Are you sure you want to delete this booking? This action cannot be undone.</p>
              </div>
            </div>
            <div className="popup-body">
              {renderSlotTiles(false)}
            </div>
            <div className="popup-btns">
              <div className="popup-btn" onClick={() => setDeleteModal(false)}>
                <span>Cancel</span>
              </div>
              <div className="popup-btn delete-btn" onClick={handleDeleteSlot}>
                <span>Delete</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Booking Popup */}
      <div className={`${showCancelModal ? 'edit-popup' : 'hide-edit'}`}>
        <div className="edit-popup-container">
          <div className="edit-popup-card">
            <div className="popup-header">
              <div className="popup-logo cancel-modal-logo">
                <img src={cross} alt="Cancel" />
              </div>
              <div className="pop-up-heading">
                <span>Cancel Booking</span>
                <p>
                  {selectedSlot?.isMultiBooking
                    ? "This is a multi-slot booking. Cancelling will cancel all related slots."
                    : "Please provide a reason to cancel this booking."
                  }
                </p>
              </div>
            </div>
            <div className="popup-body">
              <div className="popup-form">
                <div className="popup-input">
                  <span>Booking Details</span>
                  <div className="booking-details">
                    <table className="details-table" width="100%">
                      <tbody>
                        <tr>
                          <td className="label">Date</td>
                          <td className="value">{selectedSlot?.date}</td>
                          <td className="label">Time</td>
                          <td className="value">{selectedSlot?.time}</td>
                        </tr>
                        <tr>
                          <td className="label">Facility</td>
                          <td className="value">{selectedSlot?.facility}</td>
                          <td className="label">Sport</td>
                          <td className="value">{selectedSlot?.sports}</td>
                        </tr>
                        {selectedSlot?.username && (
                          <tr>
                            <td className="label">Booked By</td>
                            <td className="value">{selectedSlot.username}</td>
                            <td className="label">{selectedSlot.isRandomUser ? 'Phone' : 'Gender'}</td>
                            <td className="value">
                              {selectedSlot.isRandomUser ? selectedSlot.phone : selectedSlot.gender}
                            </td>
                          </tr>
                        )}
                        {selectedSlot?.isRandomUser ? (
                          <tr>
                            <td className="label">Booking ID</td>
                            <td className="value">{selectedSlot.numericBookingId ?? selectedSlot.bookingId}</td>
                            <td className="label"></td>
                            <td className="value"></td>
                          </tr>
                        ) : (
                          selectedSlot?.age && (
                            <tr>
                              <td className="label">Age</td>
                              <td className="value">{selectedSlot.age}</td>
                              <td className="label">Booking ID</td>
                              <td className="value">{selectedSlot.numericBookingId ?? selectedSlot.bookingId}</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="popup-input">
                  <span>Cancellation Reason</span>
                  <textarea
                    placeholder="Start typing here..."
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="popup-btns">
              <div
                className="popup-btn"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason("");
                }}
              >
                <span>Close</span>
              </div>
              <div
                className={`popup-btn delete-btn ${!cancelReason.trim() || cancelLoading ? 'disabled' : ''}`}
                onClick={() => {
                  if (!cancelReason.trim() || cancelLoading) return;
                  handleCancelBooking();
                }}
                style={{ opacity: (!cancelReason.trim() || cancelLoading) ? 0.6 : 1 }}
              >
                <span>{cancelLoading ? 'Cancelling...' : 'Cancel Booking'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingSchedule;